import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, ArrowUpRight, ClipboardList, Mail, Minus, Plus, Printer, ShoppingCart, Trash2 } from "lucide-react";
import { Link, useLocation } from "wouter";
import { StructuredData } from "@/components/StructuredData";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { siteConfig } from "@/data/site";
import {
  ACCESSORY_CART_STORAGE_KEY,
  ACCESSORY_QUOTE_CART_UPDATED_EVENT,
  accessoryCartItemCount,
  accessoryCartSubtotal,
  buildAccessoryQuoteHandoff,
  clearAccessoryQuoteCart,
  formatAccessoryCurrency,
  getAccessoryDisplayName,
  loadAccessoryQuoteCart,
  saveAccessoryQuoteCart,
  saveAccessoryQuoteHandoff,
  updateAccessoryQuoteCartQuantity,
  type AccessoryQuoteCartLine,
} from "@/lib/accessoryQuoteCart";

function CartLine({ line, onUpdate }: { line: AccessoryQuoteCartLine; onUpdate: (quantity: number) => void }) {
  const lineName = getAccessoryDisplayName(line);
  const isSingle = line.quantity === 1;

  return (
    <article className="accessory-quote-list__line">
      {line.image ? <img src={line.image} width="160" height="160" alt="" loading="lazy" /> : <span className="accessory-quote-list__image-missing" aria-hidden="true"><ClipboardList /></span>}
      <div className="accessory-quote-list__line-copy">
        <span>{line.brand} · {line.category}</span>
        <h2>{lineName}</h2>
        {line.variantLabel ? <p>{line.variantLabel}</p> : null}
        {line.sku ? <p className="accessory-quote-list__sku">SKU {line.sku}</p> : null}
        <p>{line.price === null ? "Project pricing requested" : `${formatAccessoryCurrency(line.price)} reference price each`}</p>
      </div>
      <div className="accessory-quote-list__line-actions">
        <span className="accessory-quote-list__print-quantity">Quantity: {line.quantity}</span>
        <div className="accessory-quote-list__quantity" aria-label={`Quantity for ${lineName}`}>
          <button type="button" onClick={() => onUpdate(line.quantity - 1)} aria-label={isSingle ? `Remove ${lineName} from your Quote List` : `Decrease ${lineName} quantity`}><Minus aria-hidden="true" /></button>
          <strong aria-live="polite">{line.quantity}</strong>
          <button type="button" onClick={() => onUpdate(line.quantity + 1)} aria-label={`Increase ${lineName} quantity`}><Plus aria-hidden="true" /></button>
        </div>
        <strong className="accessory-quote-list__line-total">{line.price === null ? "Project pricing pending" : formatAccessoryCurrency(line.price * line.quantity)}</strong>
        <button type="button" className="accessory-quote-list__remove" onClick={() => onUpdate(0)} aria-label={`Remove item: ${lineName}`}><Trash2 aria-hidden="true" /> Remove item</button>
      </div>
    </article>
  );
}

export default function AccessoryQuoteCart() {
  const [, setLocation] = useLocation();
  const [cart, setCart] = useState<AccessoryQuoteCartLine[]>([]);
  const [cartLoaded, setCartLoaded] = useState(false);

  useEffect(() => {
    const syncCart = () => setCart(loadAccessoryQuoteCart());
    syncCart();
    setCartLoaded(true);

    const onStorage = (event: StorageEvent) => {
      if (event.key === ACCESSORY_CART_STORAGE_KEY || event.key === null) syncCart();
    };

    window.addEventListener(ACCESSORY_QUOTE_CART_UPDATED_EVENT, syncCart);
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", syncCart);
    document.addEventListener("visibilitychange", syncCart);
    return () => {
      window.removeEventListener(ACCESSORY_QUOTE_CART_UPDATED_EVENT, syncCart);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", syncCart);
      document.removeEventListener("visibilitychange", syncCart);
    };
  }, []);

  const itemCount = accessoryCartItemCount(cart);
  const subtotal = accessoryCartSubtotal(cart);
  const summary = useMemo(() => {
    if (!cart.length) return "No models saved yet.";
    return `${itemCount} accessory item${itemCount === 1 ? "" : "s"} across ${cart.length} selected model${cart.length === 1 ? "" : "s"}.`;
  }, [cart.length, itemCount]);

  const updateQuantity = (id: string, quantity: number) => {
    const next = updateAccessoryQuoteCartQuantity(loadAccessoryQuoteCart(), id, quantity);
    saveAccessoryQuoteCart(next);
    setCart(next);
  };

  const clearCart = () => {
    clearAccessoryQuoteCart();
    setCart([]);
  };

  const continueToQuote = () => {
    const handoff = buildAccessoryQuoteHandoff(loadAccessoryQuoteCart());
    if (!handoff) return;
    saveAccessoryQuoteHandoff(handoff);
    setLocation("/request-a-quote");
  };

  const printList = () => window.print();

  const url = `${siteConfig.url}/accessory-quote-cart`;
  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    name: "Accessory Quote List | Partition Stalls",
    description: "Review saved commercial restroom accessory models, print a PDF copy, or send the selection through a secure project quote request.",
    isPartOf: { "@id": `${siteConfig.url}/#website` },
    breadcrumb: { "@id": `${url}#breadcrumb` },
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${url}#breadcrumb`,
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: siteConfig.url },
      { "@type": "ListItem", position: 2, name: "Bathroom accessories", item: `${siteConfig.url}/commercial-restroom-accessories` },
      { "@type": "ListItem", position: 3, name: "Accessory Quote List", item: url },
    ],
  };

  return (
    <>
      <StructuredData data={[pageSchema, breadcrumbSchema]} />
      <main className="accessory-quote-list-page">
        <header className="accessory-quote-list__header section-line">
          <div className="accessory-quote-list__navigation">
            <nav className="breadcrumb" aria-label="Breadcrumb"><Link href="/">Home</Link><span aria-hidden="true">/</span><Link href="/commercial-restroom-accessories">Bathroom accessories</Link><span aria-hidden="true">/</span><span aria-current="page">Quote List</span></nav>
            <Link href="/commercial-restroom-accessories#catalog-results" className="accessory-quote-list__continue text-link">Continue shopping <ArrowRight aria-hidden="true" /></Link>
          </div>
          <h1 id="accessory-quote-list-title">Your accessory Quote List.</h1>
          {cartLoaded && cart.length ? <div className="accessory-quote-list__toolbar">
            <span aria-live="polite">{summary}</span>
            <AlertDialog>
              <AlertDialogTrigger asChild><button type="button" className="accessory-quote-list__clear"><Trash2 aria-hidden="true" /> Clear cart</button></AlertDialogTrigger>
              <AlertDialogContent className="accessory-quote-list__clear-dialog">
                <AlertDialogHeader><AlertDialogTitle>Clear your entire cart?</AlertDialogTitle><AlertDialogDescription>This removes all saved accessory models and quantities so you can start a new list. It will not change any quote request you have already submitted.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Keep my items</AlertDialogCancel><AlertDialogAction className="button button--primary" onClick={clearCart}>Clear all items</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div> : null}
          {cartLoaded && cart.length ? <div className="accessory-quote-list__quick-actions"><button type="button" className="button button--primary" onClick={continueToQuote}><Mail aria-hidden="true" /> Email List for Quote</button><button type="button" className="button button--outline" onClick={printList}><Printer aria-hidden="true" /> Print / Save as PDF</button></div> : null}
        </header>

        {cartLoaded && cart.length ? <section className="accessory-quote-list section-line" aria-labelledby="accessory-quote-list-title">
          <div className="accessory-quote-list__layout">
            <div className="accessory-quote-list__lines">{cart.map((line) => <CartLine key={line.id} line={line} onUpdate={(quantity) => updateQuantity(line.id, quantity)} />)}</div>
            <aside className="accessory-quote-list__summary" aria-label="Quote List actions and pricing summary">
              <span>Saved selection</span>
              <strong>{summary}</strong>
              <div><span>Reference merchandise subtotal</span><b>{subtotal === null ? "Project pricing pending" : formatAccessoryCurrency(subtotal)}</b></div>
              <p>{subtotal === null ? "One or more selected models do not have an exact approved reference-price match. Final project pricing is confirmed in the quote." : "Freight, tax, availability, compatibility, final configuration, and project pricing are confirmed in the written quote."}</p>
              <div className="accessory-quote-list__actions">
                <button type="button" className="button button--primary" onClick={continueToQuote}><Mail aria-hidden="true" /> Email List for Quote <ArrowUpRight aria-hidden="true" /></button>
                <button type="button" className="button button--outline" onClick={printList}><Printer aria-hidden="true" /> Print / Save as PDF</button>
              </div>
              <p className="accessory-quote-list__action-note">Email List for Quote opens the secure form with your selected models and quantities already included. On submission, a PDF of this list is included with your quote request. No order or payment is created here.</p>
            </aside>
          </div>
        </section> : <section className="accessory-quote-list__empty section-line" aria-labelledby="accessory-quote-list-empty-title">
          <ShoppingCart aria-hidden="true" />
          <span className="kicker">Your saved list</span>
          <h2 id="accessory-quote-list-empty-title">{cartLoaded ? "Your Quote List is empty." : "Loading your saved list…"}</h2>
          <p>Choose models from the Bathroom Accessories catalog, then return here to review quantities, print a PDF, or send the list for a project quote.</p>
          <Link href="/commercial-restroom-accessories#catalog-results" className="button button--primary">Browse accessories <ArrowRight aria-hidden="true" /></Link>
        </section>}
      </main>
    </>
  );
}
